import type { ChangeEvent } from 'react';

import { Box, InputAdornment, Slider, Stack, TextField, Typography } from '@mui/material';

import { formatNumber } from '@shared/lib/format';

import { CollapseFilter } from '../collapse-filter';

const MIN = 0;
const MAX = 1_000_000_000;

export type AmountRangeValue = {
  from: number | null;
  to: number | null;
};

type AmountRangeFilterProps = {
  title: string;
  value: AmountRangeValue;
  onChange: (value: AmountRangeValue) => void;
};

export const AmountRangeFilter = (props: AmountRangeFilterProps) => {
  const { title, value, onChange } = props;

  const from = value.from ?? MIN;
  const to = value.to ?? MAX;

  const handleSliderChange = (_event: Event, next: number | number[]) => {
    if (!Array.isArray(next)) return;
    onChange({ from: next[0] ?? MIN, to: next[1] ?? MAX });
  };

  const handleInputChange =
    (field: keyof AmountRangeValue) => (event: ChangeEvent<HTMLInputElement>) => {
      const raw = event.target.value.replace(/\D/g, '');
      onChange({ ...value, [field]: raw ? Number(raw) : null });
    };

  const slotProps = {
    htmlInput: { inputMode: 'numeric' as const },
    input: {
      endAdornment: <InputAdornment position="end">₴</InputAdornment>,
    },
  };

  return (
    <CollapseFilter title={title}>
      <Box px={1}>
        <Slider
          min={MIN}
          max={MAX}
          step={100_000}
          value={[from, to]}
          onChange={handleSliderChange}
          valueLabelDisplay="auto"
          valueLabelFormat={formatNumber}
        />
      </Box>
      {/*
        TODO: wrap these inputs with `NumericFormat` from `react-number-format`
        (customInput={TextField}, thousandSeparator=" ", valueIsNumericString) to
        get caret-safe live grouping (e.g. "1 000 000"). Not installed yet — plain
        digit-only inputs for now.
      */}
      <Stack direction="row" spacing={1} alignItems="center">
        <TextField
          size="small"
          fullWidth
          variant="outlined"
          value={from}
          onChange={handleInputChange('from')}
          slotProps={slotProps}
        />
        <Typography>-</Typography>
        <TextField
          size="small"
          fullWidth
          variant="outlined"
          value={to}
          onChange={handleInputChange('to')}
          slotProps={slotProps}
        />
      </Stack>
    </CollapseFilter>
  );
};
